/**
 * Base authentication styles
 * These styles are used across all auth pages (sign-in, sign-up, forgot, reset)
 * Can be overridden by role-specific themes
 */

export const baseAuthStyles = {
  // Input styles
  input: {
    normal: {
      fontSize: "14px",
      lineHeight: "17px",
      textAlign: "center" as "center" | "left" | "right" | "justify",
      color: "black",
      border: "1px solid #3A7BFF",
      borderRadius: "50px",
      padding: "15px 30px",
      height: "60px",
      bgColor: "#FFF",
      boxShadow: "none !important",
    },
    error: {
      fontSize: "14px",
      lineHeight: "17px",
      textAlign: "center" as "center" | "left" | "right" | "justify",
      color: "black",
      border: "1px solid #FF6B6B",
      borderRadius: "50px",
      padding: "15px 30px",
      height: "60px",
      bgColor: "#FFF",
      boxShadow: "none !important",
    },
    placeholder: {
      fontSize: "14px",
      lineHeight: "17px",
      textAlign: "left" as "center" | "left" | "right" | "justify",
      color: "#505E70",
      border: "none !important",
      borderRadius: "50px",
      padding: "15px 30px",
      height: "60px",
      boxShadow: "none !important",
      bgColor: "transparent",
    },
    placeholderError: {
      fontSize: "14px",
      lineHeight: "17px",
      textAlign: "left" as "center" | "left" | "right" | "justify",
      color: "#FF6B6B",
      borderRadius: "50px",
      padding: "15px 30px",
      height: "60px",
      boxShadow: "none !important",
      bgColor: "transparent",
    },
  },

  // Container styles
  container: {
    maxW: "420px",
    w: "100%",
    mx: "auto",
    my: "auto",
    h: "fit-content",
    alignItems: "start" as const,
    justifyContent: "center" as const,
    flexDirection: "column" as const,
    borderRadius: "18px",
    bgColor: "#FFF",
    border: "none"
  },

  // Heading styles
  heading: {
    fontFamily: "General Sans",
    fontSize: "24px",
    fontWeight: "600",
    lineHeight: "135%",
    textAlign: "center" as const,
  },

  // Button styles
  button: {
    primary: {
      fontSize: "14px",
      lineHeight: "17px",
      variant: "brand",
      fontWeight: "700",
      padding: "14px 54px",
      borderRadius: "90px",
    },
    google: {
      py: "15px",
      px: "30px",
      height: "60px",
      fontSize: "14px",
      lineHeight: "17px",
      textTransform: "capitalize" as const,
      borderRadius: "50px",
      fontWeight: "500",
      border: "1px solid #EFEFEF",
    },
  },

  // Form control styles
  formControl: {
    errorMessage: {
      mt: 1,
      color: "#F00",
      justifyContent: "center" as const,
      bgColor: "transparent",
      padding: "0",
      borderRadius: "0",
      fontSize: "14px",
    },
  },

  // Alert styles
  alert: {
    error: {
      bgColor: "#FEE",
      color: "#F00",
      borderRadius: "8px",
      padding: "12px",
      fontSize: "14px",
    },
  },

  // Separator styles
  separator: {
    fontSize: "14px",
    color: "#0B0E10",
    p: "5px",
    textTransform: "capitalize" as const,
    whiteSpace: "nowrap" as const,
  },

  // Colors
  colors: {
    buttonBg: "blue.500",
    textColor: "navy.700",
    textColorBrand: "gray.300",
    borderColor: "gray.100",
    googleBg: "white",
    googleText: "navy.700",
  },

  // Responsive padding
  padding: {
    desktop: {
      form: "40px 32px 65px",
      footer: "30px 40px",
    },
    mobile: {
      form: "40px 32px 16px",
      footer: "16px 0px",
    },
  },
};

export type AuthStyles = {
  input?: {
    normal?: typeof baseAuthStyles.input.normal;
    error?: typeof baseAuthStyles.input.normal;
    placeholder?: Partial<typeof baseAuthStyles.input.placeholder>;
    placeholderError?: Partial<typeof baseAuthStyles.input.placeholder>;
  };
  container?: Partial<typeof baseAuthStyles.container>;
  heading?: Partial<typeof baseAuthStyles.heading>;
  button?: {
    primary?: Partial<typeof baseAuthStyles.button.primary>;
    google?: Partial<typeof baseAuthStyles.button.google>;
  };
  formControl?: {
    errorMessage?: Partial<typeof baseAuthStyles.formControl.errorMessage>;
  };
  alert?: {
    error?: Partial<typeof baseAuthStyles.alert.error>;
  };
  separator?: Partial<typeof baseAuthStyles.separator>;
  colors?: Partial<typeof baseAuthStyles.colors>;
  padding?: {
    desktop?: Partial<typeof baseAuthStyles.padding.desktop>;
    mobile?: Partial<typeof baseAuthStyles.padding.mobile>;
  };
  passwordValidation?: {
    container?: {
      bg?: string;
      borderRadius?: string;
      padding?: string;
      mt?: string;
    };
    title?: {
      fontFamily?: string;
      fontSize?: string;
      fontWeight?: string;
      color?: string;
      marginBottom?: string;
    };
    errorText?: {
      fontFamily?: string;
      fontSize?: string;
      fontWeight?: string;
      color?: string;
    };
  };
};
